import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandGlyph } from '@/components/BrandMark';

// Suite intro splash — mirrors the IsoSofts (Architecture/Algebra) intro,
// branded for ESD. No FontAwesome (ESD uses inline SVG).

const BADGES = ['eIDAS', 'PKI', 'Audit Trail'];

// Minimal 24×24 stroke glyphs for the module row (e-signature themed).
const GLYPHS = [
  'M3 7l9 6 9-6M3 7v10h18V7M3 7l9-4 9 4', // envelope
  'M4 20h16M5 16l9-9 3 3-9 9H5v-3z', // pen / sign
  'M7 3h7l5 5v13H7zM14 3v5h5', // document
  'M12 3l8 3v6c0 4-3 7-8 9-5-2-8-5-8-9V6z', // shield
  'M16 19a4 4 0 00-8 0M12 11a3 3 0 100-6 3 3 0 000 6M20 19a3 3 0 00-5-2', // users
  'M12 7v5l3 2M12 21a9 9 0 100-18 9 9 0 000 18', // clock
  'M5 12l4 4 10-10', // check
  'M4 7h16v6a4 4 0 01-4 4H8a4 4 0 01-4-4zM9 21h6M12 17v4', // stamp
];

export function IntroScreen({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'enter' | 'exit'>('enter');
  const [barWidth, setBarWidth] = useState(0);
  const [badgeVisible, setBadgeVisible] = useState([false, false, false]);
  const [modulesVisible, setModulesVisible] = useState(false);

  useEffect(() => {
    const ids: number[] = [];
    BADGES.forEach((_, i) => {
      ids.push(window.setTimeout(() => {
        setBadgeVisible((prev) => { const n = [...prev]; n[i] = true; return n; });
      }, 400 + i * 180));
    });
    ids.push(window.setTimeout(() => setModulesVisible(true), 900));
    ids.push(window.setTimeout(() => setBarWidth(100), 200));
    ids.push(window.setTimeout(() => setPhase('exit'), 2600));
    ids.push(window.setTimeout(() => onDone(), 3100));
    return () => ids.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
        transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
        opacity: phase === 'exit' ? 0 : 1,
        transform: phase === 'exit' ? 'scale(1.04)' : 'scale(1)',
        pointerEvents: phase === 'exit' ? 'none' : 'all',
        overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(59,130,246,0.08) 1px, transparent 0)',
        backgroundSize: '40px 40px',
      }} />
      <div style={{ position: 'absolute', top: '20%', left: '15%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '20%', right: '15%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'introFadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both' }}>
        {/* Logo mark — IsoSofts marka işareti */}
        <div style={{
          width: 72, height: 72, borderRadius: 20,
          background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
          boxShadow: '0 0 0 1px rgba(99,102,241,0.4), 0 0 40px rgba(59,130,246,0.3)',
          animation: 'introLogoSpin 0.8s cubic-bezier(0.16,1,0.3,1) both',
        }}>
          <BrandGlyph size={32} />
        </div>

        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <h1 style={{
            fontSize: 48, fontWeight: 800, margin: 0, letterSpacing: '-1px',
            background: 'linear-gradient(90deg, #60a5fa, #a5b4fc, #60a5fa)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            animation: 'introShimmer 2s linear infinite',
          }}>
            Documentology
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 15, color: 'rgba(148,163,184,0.9)', letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 500 }}>
            ISOSofts Platform
          </p>
        </div>

        <p style={{ margin: '16px 0 0', fontSize: 13, lineHeight: 1.65, color: 'rgba(100,116,139,0.85)', letterSpacing: '0.02em', textAlign: 'center', maxWidth: 560, padding: '0 20px' }}>
          {t('product.description')}
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          {BADGES.map((b, i) => (
            <span key={b} style={{
              padding: '4px 12px', borderRadius: 999,
              border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd', fontSize: 11, fontWeight: 600,
              letterSpacing: '0.08em', background: 'rgba(59,130,246,0.08)',
              transition: 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.16,1,0.3,1)',
              opacity: badgeVisible[i] ? 1 : 0,
              transform: badgeVisible[i] ? 'translateY(0)' : 'translateY(8px)',
            }}>{b}</span>
          ))}
        </div>

        <div style={{
          display: 'flex', gap: 12, marginTop: 30,
          transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.16,1,0.3,1)',
          opacity: modulesVisible ? 1 : 0,
          transform: modulesVisible ? 'translateY(0)' : 'translateY(12px)',
        }}>
          {GLYPHS.map((d, i) => (
            <div key={i} style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(59,130,246,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: `opacity 0.3s ease ${i * 50}ms, transform 0.3s cubic-bezier(0.16,1,0.3,1) ${i * 50}ms`,
              opacity: modulesVisible ? 1 : 0,
              transform: modulesVisible ? 'scale(1)' : 'scale(0.8)',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={d} />
              </svg>
            </div>
          ))}
        </div>

        <div style={{ width: 240, marginTop: 36, height: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
            width: `${barWidth}%`, transition: 'width 2400ms cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 0 8px rgba(99,102,241,0.6)',
          }} />
        </div>

        <p style={{ margin: '12px 0 0', fontSize: 11, color: 'rgba(100,116,139,0.6)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          Loading your workspace
        </p>
      </div>

      <style>{`
        @keyframes introFadeUp { from { opacity: 0; transform: translateY(24px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes introLogoSpin { from { opacity: 0; transform: rotate(-15deg) scale(0.7);} to { opacity: 1; transform: rotate(0deg) scale(1);} }
        @keyframes introShimmer { 0% { background-position: 0% center;} 100% { background-position: 200% center;} }
      `}</style>
    </div>
  );
}

// Plays the intro once per full page load — mirrors Algebra/IsoSofts, which show
// the splash on every fresh load (e.g. every "Go To ESD" from the suite) but NOT
// on in-app SPA navigations. A module-level flag (not sessionStorage) gives exactly
// that: it resets on a real reload yet survives client-side route changes.
let introPlayed = false;

export function IntroOnce() {
  const [done, setDone] = useState(introPlayed);
  if (done) return null;
  return (
    <IntroScreen
      onDone={() => {
        introPlayed = true;
        setDone(true);
      }}
    />
  );
}
